import React, { useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { DemandCarliChat } from './DemandCarliChat';
import { DemandPlatformConnections } from './DemandPlatformConnections';
import { DemandOpportunity } from './DemandOpportunity';
import { DemandAdStudio } from './DemandAdStudio';
import { DemandCampaigns } from './DemandCampaigns';
import { DemandSocial } from './DemandSocial';
import { DemandBriefing } from './DemandBriefing';
import { DemandWorkRail } from './DemandWorkRail';
import { DemandSnapshots } from './DemandSnapshots';
import { demandMetrics } from '../data/demandData';
type DemandScope = {
  label: string;
  detail: string;
};
const tabs = ['Demand HQ', 'Opportunities', 'Ad Studio', 'Campaigns', 'Social', 'Connections'] as const;
type DemandTab = (typeof tabs)[number];
function getDemandScope(selectedOwner: string): DemandScope {
  if (selectedOwner === 'Eddy') return {
    label: 'Eddy',
    detail: 'Eddy inventory and approved channel context'
  };
  if (selectedOwner === '1 People') return {
    label: '1 People',
    detail: '1 People inventory and approved channel context'
  };
  if (selectedOwner) return {
    label: selectedOwner,
    detail: 'Selected brand inventory and approved channel context'
  };
  return {
    label: 'All available brands',
    detail: 'Live inventory, campaign, and channel context across approved brands'
  };
}
export function DemandDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const scope = getDemandScope(selectedOwner);
  const [tab, setTab] = useState<DemandTab>('Demand HQ');
  const [workspaceOpen, setWorkspaceOpen] = useState(true);
  const workRailRef = useRef<HTMLDivElement>(null);
  return <div className="mx-auto max-w-[1500px] px-5 py-6 sm:px-8 lg:py-8" aria-labelledby="demand-heading" data-id="element-1071" data-mp="38p91rlj8qmw3">
      <h1 id="demand-heading" className="sr-only" data-id="element-1072" data-mp="2zqxffvpeqb9g">Carli for Demand</h1>

      <div className="border-b border-ink/10" role="tablist" aria-label="Demand sections" data-id="element-1073" data-mp="2zq5hie8c81g0">
        <div className="-mb-px flex gap-1 overflow-x-auto" data-id="element-1074" data-mp="2uscchoirtdhb">
          {tabs.map(option => {
          const active = tab === option;
          return <button key={option} type="button" role="tab" aria-selected={active} onClick={() => setTab(option)} className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${active ? 'border-[#BB5E40] text-[#94452E]' : 'border-transparent text-ink/55 hover:text-ink'}`} data-id="element-1075" data-mp="2q3l3yhxrjb78">
                {option}
              </button>;
        })}
        </div>
      </div>

      <div className="mt-7" data-id="element-1076" data-mp="2u2ty3nwptb3b">
        {tab === 'Demand HQ' && <>
            <section aria-label="Demand performance" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1077" data-mp="2z2evvv05fdeg">
              {demandMetrics.map(metric => <div key={metric.label} className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1078" data-mp="3rktzf582uyy0">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-1079" data-mp="1g4s2j800fjen">{metric.label}</p>
                  <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1080" data-mp="1b70beg76w1bi">{metric.value}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-ink/50" data-id="element-1081" data-mp="1pu3agrb5ui0y">{metric.detail}</p>
                </div>)}
            </section>

            <div className="mt-4" data-id="element-1082" data-mp="x6fszl56s7ij">
              <DemandBriefing onSelectSignal={id => {
            if (id === 'approvals') setTab('Ad Studio');else if (id === 'attention') setTab('Campaigns');else workRailRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }} data-id="element-1083" data-mp="x5nyyer8mhud" />
            </div>

            <section aria-label="Carli collaboration workspace" className="mt-7" data-id="element-1084" data-mp="127wsbc80vlx0">
              <button type="button" onClick={() => setWorkspaceOpen(open => !open)} aria-expanded={workspaceOpen} className={`flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left ${workspaceOpen ? 'mb-3' : 'border border-ink/10 bg-paper shadow-sm'}`} data-id="element-1085" data-mp="2hkhja94bh45r">
                <span className="flex items-center gap-2 text-sm font-semibold" data-id="element-1086" data-mp="3hvap1vv59tl3">
                  <span aria-hidden="true" className="h-4 w-4 shrink-0 bg-[#BB5E40]" style={{
                WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
                maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain'
              }} data-id="element-1087" data-mp="3mudlwt0z0thz" />
                  Carli workspace
                  <span className="hidden text-xs font-normal text-ink/50 sm:inline" data-id="element-1088" data-mp="23xaahmqhfnw7">· Conversations, campaign rooms, and team channels</span>
                </span>
                {workspaceOpen ? <ChevronDownIcon className="h-4 w-4 text-ink/45" data-id="element-1089" data-mp="1tykszv2i05gh" /> : <ChevronRightIcon className="h-4 w-4 text-ink/45" data-id="element-1090" data-mp="1oyyb38bq611c" />}
              </button>
              {workspaceOpen && <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1091" data-mp="2mlf78a8q28li">
                  <DemandCarliChat scopeLabel={scope.label} scopeDetail={scope.detail} data-id="element-1092" data-mp="2hmu7ebxdtqxl" />
                  <div ref={workRailRef} className="scroll-mt-6" data-id="element-1093" data-mp="2cwdssohtrz9n">
                    <DemandWorkRail onViewProjects={() => setTab('Campaigns')} data-id="element-1094" data-mp="2cukk73nnj4z0" />
                  </div>
                </div>}
            </section>

            <div className="mt-8" data-id="element-1095" data-mp="35dzw7glavtk1">
              <DemandSnapshots onGoToOpportunities={() => setTab('Opportunities')} onGoToCampaigns={() => setTab('Campaigns')} onGoToSocial={() => setTab('Social')} data-id="element-1096" data-mp="35f197qlvmq4e" />
            </div>
          </>}

        {tab === 'Opportunities' && <DemandOpportunity selectedOwner={selectedOwner} data-id="element-1097" data-mp="13mzitjizldje" />}
        {tab === 'Ad Studio' && <DemandAdStudio selectedOwner={selectedOwner} data-id="element-1098" data-mp="oyd1jc9subpj" />}
        {tab === 'Campaigns' && <DemandCampaigns selectedOwner={selectedOwner} data-id="element-1099" data-mp="twxs0jz91h0s" />}
        {tab === 'Social' && <DemandSocial data-id="element-1100" data-mp="fxolyd4rc3aj" />}
        {tab === 'Connections' && <DemandPlatformConnections data-id="element-1101" data-mp="fwwqr3f2o7ks" />}
      </div>
    </div>;
}